import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { styled } from "@mui/system";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useAuth } from "../../../../../context";
import ListUltrasoundReferral from "./ListUltrasoundReferral";
import { useTranslation } from "react-i18next";
import { getLocalDateString } from "../../../../../utils";

// Styled Components for Full-Width Layout
const FullWidthPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#ffffff",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#3a3a3a",
  textAlign: "left",
  marginBottom: theme.spacing(4),
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.shape.borderRadius,
  },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.primary.main,
  },
  "& .MuiFormLabel-root.Mui-focused": {
    color: theme.palette.primary.main,
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 700,
  fontSize: "1rem",
  padding: "12px 16px",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const getCurrentDate = () => new Date().toISOString().split("T")[0];

const UltrasoundReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const today = getLocalDateString();
  const [ticketSearch, setTicketSearch] = useState("");
  const selectedTicketSnapshot = useRef(null);

  const { data: ultrasoundTypesData } = useFetchObjects(
    ["ultrasound-types"],
    "ultrasound-types/",
    token
  );
  const effectiveTicketSearch = selectedTicketSnapshot.current ? "" : ticketSearch;
  const { data: ticketsData } = useFetchObjects(
    ["tickets-ultrasound-referral", today, effectiveTicketSearch],
    `tickets/?visit_date=${today}&search=${encodeURIComponent(effectiveTicketSearch)}&page_size=200`,
    token,
    { enabled: !!token }
  );

  const { handleAdd, resetForm } = useAdd("ultrasounds", token);

  const ultrasoundTypes = ultrasoundTypesData || [];
  const tickets = ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : []);

  const [ultrasoundDetails, setUltrasoundDetails] = useState({
    ultrasoundType: "",
    date: getCurrentDate(),
    isUrgent: false,
    totalAmount: "",
    ticket: null,
    doctor: auth?.user?.user?.id,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (resetForm) {
      setUltrasoundDetails({
        ultrasoundType: "",
        date: getCurrentDate(),
        isUrgent: false,
        totalAmount: "",
        ticket: null,
        doctor: auth?.user?.user?.id,
      });
      setErrors({});
      selectedTicketSnapshot.current = null;
    }
  }, [resetForm]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setUltrasoundDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "ultrasoundType") {
      const selectedType = ultrasoundTypes?.find(
        (type) => type.id.toString() === value
      );
      setUltrasoundDetails((prev) => ({
        ...prev,
        totalAmount: selectedType ? selectedType.cost.toString() : "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!ultrasoundDetails.ticket) {
      tempErrors.ticket = t("doctor.selectTicketRequired");
    }
    if (!ultrasoundDetails.ultrasoundType) {
      tempErrors.ultrasoundType = t("doctor.ultTypeRequired");
    }
    if (!ultrasoundDetails.date) {
      tempErrors.date = t("doctor.testDateRequired");
    }
    if (!ultrasoundDetails.totalAmount) {
      tempErrors.totalAmount = t("doctor.totalAmountRequired");
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const patientId = typeof ultrasoundDetails.ticket.patient === "object" ? ultrasoundDetails.ticket.patient?.id : ultrasoundDetails.ticket.patient;
      const payload = {
        patient: patientId,
        ticket: ultrasoundDetails.ticket.id,
        doctor: ultrasoundDetails.doctor,
        ultrasound_type: ultrasoundDetails.ultrasoundType,
        date: ultrasoundDetails.date,
        is_urgent: ultrasoundDetails.isUrgent,
        total_amount: ultrasoundDetails.totalAmount,
      };
      handleAdd(payload);
    }
  };

  return (
    <FullWidthPaper>
      <SectionTitle>{t("doctor.ultReferralForm")}</SectionTitle>

      <Grid container spacing={3}>
        {/* Ticket selection – patient comes from ticket */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tickets}
            getOptionLabel={(option) =>
              option ? `${option.ticket_number || option.id}${option.patient_name_display ? ` - ${option.patient_name_display}` : ""}` : ""
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("print.ticketNumber")}
                placeholder={t("doctor.selectTicketPlaceholder")}
                variant="outlined"
                fullWidth
                error={!!errors.ticket}
                helperText={errors.ticket}
              />
            )}
            value={ultrasoundDetails.ticket}
            inputValue={ticketSearch}
            onInputChange={(e, value) => {
              setTicketSearch(value);
              if (!value) selectedTicketSnapshot.current = null;
            }}
            onChange={(event, newValue) => {
              selectedTicketSnapshot.current = newValue ? { id: newValue.id } : null;
              setUltrasoundDetails((prev) => ({ ...prev, ticket: newValue }));
              setErrors((prev) => ({ ...prev, ticket: "" }));
            }}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        {ultrasoundDetails.ticket && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("laboratory.patientName")}
              value={ultrasoundDetails.ticket.patient_name_display ?? ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        {/* Ultrasound Type Dropdown */}
        <Grid item xs={12} sm={6}>
          <StyledFormControl fullWidth error={!!errors.ultrasoundType}>
            <InputLabel>{t("doctor.ultrasoundType")}</InputLabel>
            <Select
              name="ultrasoundType"
              value={ultrasoundDetails.ultrasoundType}
              onChange={handleChange}
              displayEmpty
            >
              {ultrasoundTypes?.map((type) => (
                <MenuItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.ultrasoundType && (
              <Typography color="error" variant="caption">
                {errors.ultrasoundType}
              </Typography>
            )}
          </StyledFormControl>
        </Grid>

        {/* Test Date */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="date"
            label={t("doctor.testDate")}
            type="date"
            fullWidth
            value={ultrasoundDetails.date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.date}
            helperText={errors.date}
          />
        </Grid>

        {/* Total Amount */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="totalAmount"
            label={t("doctor.totalAmount")}
            type="number"
            fullWidth
            value={ultrasoundDetails.totalAmount}
            onChange={handleChange}
            error={!!errors.totalAmount}
            helperText={errors.totalAmount}
          />
        </Grid>

        {/* Urgent Switch */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={ultrasoundDetails.isUrgent}
                onChange={handleChange}
                name="isUrgent"
              />
            }
            label={t("doctor.urgent")}
          />
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <StyledButton fullWidth onClick={handleSubmit}>
            {t("doctor.submit")}
          </StyledButton>
        </Grid>
      </Grid>

      {/* List of Referrals */}
      <Box mt={5}>
        <ListUltrasoundReferral />
      </Box>
    </FullWidthPaper>
  );
};

export default UltrasoundReferral;
